// Writes a raw HTML table in one layout, one row per line group, attributes kept in
// order. Returns null when the block is not a plain table.
function textNodes(root: Node): Text[] {
	const texts: Text[] = [];
	for (const child of Array.from(root.childNodes)) {
		if (child.nodeType === 3) texts.push(child as Text);
		else texts.push(...textNodes(child));
	}
	return texts;
}

// The table's one layout, from its DOM. Null when it holds anything but a caption and sections of rows of cells.
export function layout(table: Element): string | null {
	const attr = (value: string) => value.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
	const attrs = (el: Element) => Array.from(el.attributes, (a) => ` ${a.name}="${attr(a.value)}"`).join("");
	// Indentation is source layout everywhere but inside <pre> and white-space: pre elements.
	const kept = (node: Node, root: Element): boolean => {
		for (let n: Node | null = node; n && n !== root.parentNode; n = n.parentNode) {
			const el = n.nodeType === 1 ? (n as Element) : null;
			if (el && (el.tagName === "PRE" || /white-space\s*:\s*pre/i.test(el.getAttribute("style") ?? ""))) return true;
		}
		return false;
	};
	const inner = (el: Element) => {
		const copy = el.cloneNode(true) as Element;
		const texts = textNodes(copy).filter((text) => !kept(text, copy));
		for (const text of texts) text.data = text.data.replace(/\n\s*/g, "\n");
		return kept(copy, copy) ? copy.innerHTML : copy.innerHTML.trim();
	};
	const out = [`<div class="overflow-x-auto">`, `  <table${attrs(table)}>`];
	for (const part of Array.from(table.children)) {
		const tag = part.tagName.toLowerCase();
		if (tag === "caption") {
			out.push(`    <caption${attrs(part)}>${inner(part)}</caption>`);
			continue;
		}
		if (!["thead", "tbody", "tfoot"].includes(tag)) return null;
		out.push(`    <${tag}${attrs(part)}>`);
		for (const row of Array.from(part.children)) {
			if (row.tagName !== "TR") return null;
			out.push(`      <tr${attrs(row)}>`);
			for (const cell of Array.from(row.children)) {
				const name = cell.tagName.toLowerCase();
				if (name !== "td" && name !== "th") return null;
				out.push(`        <${name}${attrs(cell)}>${inner(cell)}</${name}>`);
			}
			out.push("      </tr>");
		}
		out.push(`    </${tag}>`);
	}
	out.push("  </table>", "</div>");
	return out.join("\n");
}

export function canonicalTable(raw: string): string | null {
	const m = /^<div class="overflow-x-auto">\s*(<table\b[\s\S]*<\/table>)\s*<\/div>$/.exec(raw.trim());
	if (!m) return null;
	const doc = new DOMParser().parseFromString(m[1], "text/html");
	const table = doc.querySelector("table");
	return !table || doc.body.children.length !== 1 ? null : layout(table);
}
